/**
 * Service for validating workflow definitions against Azure Logic Apps.
 * Uses the Azure Management API validate endpoint, or local runtime in local mode.
 */

import { useNotificationStore } from '@/stores/notificationStore';
import { isLocalMode } from './localRuntimeClient';
import { telemetryService } from './telemetryService';
import { appFetch, AppFetchError, getAppUrl } from './appClient';
import { parseJsonBody } from './apiError';
import { apiVersions } from './endpoints';
import type { ConnectionsConfiguration } from '@/types/logicApps';

export interface ValidationError {
  type: string;
  message: string;
  path?: string;
  severity?: string;
}

export interface ValidationResult {
  isValid: boolean;
  validationCount: number;
  errors: ValidationError[];
}

export interface ValidateWorkflowOptions {
  projectName: string;
  appName: string;
  getToken: () => Promise<string | null>;
  workflowName: string;
  workflowDefinition: Record<string, unknown>;
  /**
   * In-memory connections to validate against (typically the draft connections
   * the user is currently editing). When omitted, the backend falls back to
   * whatever is committed on disk in connections.json.
   */
  connections?: ConnectionsConfiguration;
}

/**
 * Validates a workflow definition against Azure Logic Apps validation API.
 */
export async function validateWorkflowWithAzure(
  options: ValidateWorkflowOptions
): Promise<ValidationResult> {
  const {
    getToken,
    workflowName,
    workflowDefinition,
    connections,
  } = options;

  const allErrors: ValidationError[] = [];

  // Step 1: Local validation
  if (!workflowDefinition.definition) {
    allErrors.push({
      type: 'Schema',
      message: 'Missing required "definition" property',
    });
  } else {
    const definition = workflowDefinition.definition as Record<string, unknown>;

    if (!definition.$schema) {
      allErrors.push({
        type: 'Schema',
        path: '$schema',
        message: 'Missing required "$schema" property',
      });
    }

    if (!definition.contentVersion) {
      allErrors.push({
        type: 'Schema',
        path: 'contentVersion',
        message: 'Missing required "contentVersion" property',
      });
    }

    if (!definition.triggers || typeof definition.triggers !== 'object') {
      allErrors.push({
        type: 'Schema',
        path: 'triggers',
        message: 'Workflow should have a "triggers" object',
      });
    }
  }

  // Step 2: Azure API validation (or local runtime validation)
  try {
    const url = `${getAppUrl(options)}/workflows/${workflowName}/validate?api-version=${apiVersions.otto}`;

    // Strip client-only fields (e.g. notes) that Azure doesn't recognize
    const { notes: _notes, ...azureProperties } = workflowDefinition as Record<string, unknown>;

    // Include connections so the backend resolves references against the
    // in-progress draft state (not what's currently committed on disk).
    if (connections && Object.keys(connections).length > 0) {
      azureProperties.connections = connections;
    }

    const requestBody = {
      properties: azureProperties,
    };

    let response: Response;

    if (isLocalMode()) {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
    } else {
      // Cloud mode: use appFetch for token auth
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Failed to acquire authentication token');
        }

        await appFetch(url, token, {
          method: 'POST',
          body: JSON.stringify(requestBody),
        });
        // appFetch succeeded — validation passed
        response = new Response(null, { status: 200 });
      } catch (error) {
        // appFetch throws AppFetchError with structured status and body
        if (error instanceof AppFetchError) {
          response = new Response(error.body, { status: error.status });
        } else {
          const errMsg = error instanceof Error ? error.message : String(error);
          response = new Response(errMsg, { status: 400 });
        }
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${errorText}`;
      let errorCode = '';

      // Try to parse error as JSON for better formatting
      const errorJson = parseJsonBody(errorText);
      if (typeof errorJson === 'string') {
        errorMessage = errorJson;
      } else if (errorJson && typeof errorJson === 'object') {
        const obj = errorJson as { error?: { code?: string; message?: string }; message?: string };
        errorCode = obj.error?.code || '';
        errorMessage = obj.error?.message || obj.message || errorMessage;
      }

      // Agent connection errors are expected when connections exist locally
      // but haven't been deployed yet — downgrade to warning
      const isAgentConnectionError = errorCode === 'AgentModelConfigurationReferenceNotFound'
        || errorCode === 'AgentConnectionResourceIdInvalid';

      allErrors.push({
        type: 'AzureApi',
        message: errorMessage,
        severity: isAgentConnectionError ? 'Warning' : 'Error',
      });
    }
  } catch (error: unknown) {
    allErrors.push({
      type: 'AzureApi',
      message: `Could not perform Azure validation: ${error instanceof Error ? error.message : 'Unknown error'}`,
      severity: 'Warning',
    });
  }

  const result: ValidationResult = {
    isValid: allErrors.length === 0,
    validationCount: allErrors.length,
    errors: allErrors,
  };

  const hasBlockingErrors = allErrors.some((e) => e.severity !== 'Warning');

  telemetryService.trackEvent('workflow.validation.completed', {
    errorCount: result.validationCount,
    hasBlockingErrors,
  });

  return result;
}

/**
 * Validates the workflow and adds a notification with the result.
 */
export async function validateAndNotify(
  options: ValidateWorkflowOptions
): Promise<ValidationResult> {
  const result = await validateWorkflowWithAzure(options);
  const { addNotification } = useNotificationStore.getState();

  if (result.isValid) {
    addNotification({
      type: 'success',
      title: 'Validation Passed',
      message: `Workflow "${options.workflowName}" is valid and ready to run.`,
    });
  } else {
    addNotification({
      type: 'error',
      title: 'Validation Failed',
      message: `Found ${result.validationCount} issue${result.validationCount !== 1 ? 's' : ''} in workflow "${options.workflowName}".`,
      validationErrors: result.errors,
    });
  }

  return result;
}
